import { hyp } from '../../core/exactMath';
import type { OwnerId } from '../ids';
import { COMBAT, type DamageType } from './rules';

export type TargetRef = { readonly kind: 'unit' | 'building'; readonly id: number };

/** Read-only, serialisable view of a projectile in flight. Grid coordinates. */
export interface Projectile {
  readonly id: number;
  /** Unit that fired it (may have died since). */
  readonly attackerId: number;
  readonly ownerId: OwnerId;
  readonly x: number;
  readonly y: number;
  readonly target: TargetRef;
  /** Where it is heading: the target's position, or its last known position once the target is gone. */
  readonly aim: { readonly x: number; readonly y: number };
  /** Tiles per second. */
  readonly speed: number;
  readonly damage: number;
  readonly damageType: DamageType;
  /** Seconds in flight. */
  readonly age: number;
}

type MutableProjectile = { -readonly [K in keyof Projectile]: Projectile[K] } & { aim: { x: number; y: number } };

export interface ProjectileWorld {
  /** Where a target is now, or null if it no longer exists. */
  targetPoint(t: TargetRef): { x: number; y: number } | null;
}

export type ProjectileOutcome = { readonly projectile: Projectile; readonly hit: boolean };

/**
 * Projectiles in flight (DECISIONS D64). Each one homes on its target's
 * current position at a fixed speed and lands when it reaches it; damage is
 * applied by the caller on landing. If the target is gone it flies on to the
 * last known position and fizzles. Deterministic: processed in id order.
 */
export class Projectiles {
  private readonly list = new Map<number, MutableProjectile>();
  private nextId = 1;

  get count(): number {
    return this.list.size;
  }

  get nextProjectileId(): number {
    return this.nextId;
  }

  fire(p: Omit<Projectile, 'id' | 'age'>): Projectile {
    const m: MutableProjectile = { ...p, aim: { ...p.aim }, id: this.nextId++, age: 0 };
    this.list.set(m.id, m);
    return snap(m);
  }

  all(): Projectile[] {
    return [...this.list.keys()].sort((a, b) => a - b).map((id) => snap(this.list.get(id)!));
  }

  /** Move every projectile; returns the ones that landed this step (hit or fizzled), in id order. */
  step(dt: number, world: ProjectileWorld): ProjectileOutcome[] {
    const out: ProjectileOutcome[] = [];
    if (!(dt > 0)) return out;
    for (const id of [...this.list.keys()].sort((a, b) => a - b)) {
      const p = this.list.get(id)!;
      const now = world.targetPoint(p.target);
      if (now) p.aim = { x: now.x, y: now.y };
      p.age += dt;
      const dx = p.aim.x - p.x;
      const dy = p.aim.y - p.y;
      const d = hyp(dx, dy);
      const travel = p.speed * dt;
      if (d <= travel + 1e-9) {
        p.x = p.aim.x;
        p.y = p.aim.y;
        this.list.delete(id);
        out.push({ projectile: snap(p), hit: now !== null });
      } else if (p.age >= COMBAT.projectileMaxSeconds) {
        this.list.delete(id);
        out.push({ projectile: snap(p), hit: false });
      } else {
        p.x += (dx / d) * travel;
        p.y += (dy / d) * travel;
      }
    }
    return out;
  }
}

function snap(p: MutableProjectile): Projectile {
  return { ...p, aim: { ...p.aim }, target: { ...p.target } };
}
